import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

function LPManager({ lps, selectedLp, onSelect, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [html, setHtml] = useState('');
  const [creating, setCreating] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [newlyCreatedLp, setNewlyCreatedLp] = useState(null);
  const [copied, setCopied] = useState(false);

  const getServerUrl = () => {
    return window.location.hostname === 'localhost'
      ? `${window.location.protocol}//${window.location.hostname}:3848`
      : `${window.location.protocol}//${window.location.host}`;
  };

  const isUrl = (str) => /^https?:\/\//i.test(str.trim());
  const canCreate = name.trim() && (html.trim() || url.trim());

  // URLからHTMLを自動取得
  const fetchHtmlFromUrl = async () => {
    if (!url.trim() || !isUrl(url)) return;
    setFetching(true);
    try {
      const resp = await fetch(`${API}/fetch-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await resp.json();
      if (data.html) {
        setHtml(data.html);
      } else {
        alert('HTMLの取得に失敗しました: ' + (data.error || '不明なエラー'));
      }
    } catch (e) {
      alert('HTMLの取得に失敗しました');
    }
    setFetching(false);
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const body = { name: name.trim() };
      if (html.trim()) {
        body.type = 'html';
        body.html = html;
        body.url = url.trim() || null;
      } else if (url.trim()) {
        body.type = 'url';
        body.url = url.trim();
      }
      const resp = await fetch(`${API}/lps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const lp = await resp.json();
      setName('');
      setUrl('');
      setHtml('');
      setShowForm(false);
      setNewlyCreatedLp(lp);
      onSelect(lp.id);
      onRefresh();
    } catch (e) {
      console.error('Failed to create LP:', e);
    }
    setCreating(false);
  };

  const copyTag = (lpId) => {
    const tag = `<script src="${getServerUrl()}/tracker/heatmap-tracker.js" data-lp-id="${lpId}"></script>`;
    navigator.clipboard.writeText(tag);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('このLPを削除しますか？関連データも全て削除されます。')) return;
    await fetch(`${API}/lps/${id}`, { method: 'DELETE' });
    if (selectedLp === id) onSelect(null);
    onRefresh();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setHtml(ev.target.result);
      if (!name.trim()) setName(file.name.replace(/\.html?$/, ''));
    };
    reader.readAsText(file);
  };

  return (
    <div className="lp-manager">
      <div className="lp-manager-header">
        <h3>LP一覧</h3>
        <button className="lp-add-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? '閉じる' : '+ LP追加'}
        </button>
      </div>

      {showForm && (
        <div className="lp-form">
          <input
            type="text"
            placeholder="LP名（例：商品A ランディングページ）"
            value={name}
            onChange={e => setName(e.target.value)}
            className="lp-form-input"
          />
          <div className="lp-form-url-row">
            <input
              type="text"
              placeholder="LPのURL（例：https://example.com/lp）"
              value={url}
              onChange={e => {
                setUrl(e.target.value);
                if (!name.trim() && isUrl(e.target.value)) {
                  try { setName(new URL(e.target.value).hostname); } catch {}
                }
              }}
              className="lp-form-input"
            />
            <button
              className="lp-fetch-btn"
              onClick={fetchHtmlFromUrl}
              disabled={fetching || !isUrl(url)}
              title="URLからHTMLを取得"
            >
              {fetching ? '取得中...' : 'HTML取得'}
            </button>
          </div>
          <div className="lp-form-divider">
            <span>または</span>
          </div>
          <div className="lp-form-upload">
            <label className="lp-upload-btn">
              HTMLファイルを選択
              <input type="file" accept=".html,.htm" onChange={handleFileUpload} style={{ display: 'none' }} />
            </label>
            {html && <span className="lp-upload-status">({(html.length / 1024).toFixed(1)} KB)</span>}
          </div>
          <textarea
            placeholder="HTMLソースを直接貼り付けることもできます"
            value={html}
            onChange={e => setHtml(e.target.value)}
            className="lp-form-textarea"
            rows={4}
          />
          <button
            className="lp-create-btn"
            onClick={handleCreate}
            disabled={creating || !canCreate}
          >
            {creating ? '登録中...' : 'LPを登録'}
          </button>
        </div>
      )}

      <div className="lp-list">
        <div
          className={`lp-item ${!selectedLp ? 'active' : ''}`}
          onClick={() => onSelect(null)}
        >
          <div className="lp-item-info">
            <span className="lp-item-name">サンプルLP（デフォルト）</span>
            <span className="lp-item-meta">sample-lp.html</span>
          </div>
        </div>
        {lps.map(lp => (
          <div
            key={lp.id}
            className={`lp-item ${selectedLp === lp.id ? 'active' : ''}`}
            onClick={() => onSelect(lp.id)}
          >
            <div className="lp-item-info">
              <span className="lp-item-name">{lp.name}</span>
              <span className="lp-item-meta">
                ID: {lp.id} | {new Date(lp.createdAt).toLocaleDateString('ja-JP')}
              </span>
            </div>
            <div className="lp-item-actions">
              <button className="lp-delete-btn" onClick={(e) => handleDelete(lp.id, e)} title="削除">
                x
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 新規LP作成後のタグ発行モーダル */}
      {newlyCreatedLp && (
        <div className="lp-tag-modal-overlay" onClick={() => setNewlyCreatedLp(null)}>
          <div className="lp-tag-modal" onClick={e => e.stopPropagation()}>
            <h3>LP登録完了 - トラッキングタグ発行</h3>
            <p className="lp-tag-success">「{newlyCreatedLp.name}」が登録されました</p>
            <div className="lp-tag-section">
              <label>トラッキングタグ（LPの&lt;/body&gt;直前に貼り付けてください）:</label>
              <div className="lp-tag-code-wrapper">
                <code className="lp-tag-code">{`<script src="${getServerUrl()}/tracker/heatmap-tracker.js" data-lp-id="${newlyCreatedLp.id}"></script>`}</code>
                <button className="lp-copy-btn" onClick={() => copyTag(newlyCreatedLp.id)}>
                  {copied ? 'コピー済み!' : 'コピー'}
                </button>
              </div>
            </div>
            <div className="lp-tag-section">
              <label>LP ID:</label>
              <code className="lp-tag-id">{newlyCreatedLp.id}</code>
            </div>
            <div className="lp-tag-section">
              <label>プレビューURL:</label>
              <a href={`${getServerUrl()}/lp/${newlyCreatedLp.id}`} target="_blank" rel="noreferrer" className="lp-preview-url">
                {getServerUrl()}/lp/{newlyCreatedLp.id}
              </a>
            </div>
            <div className="lp-tag-note">
              このタグは後から「LP一覧」で再確認できます。
            </div>
            <button className="lp-tag-close-btn" onClick={() => setNewlyCreatedLp(null)}>閉じる</button>
          </div>
        </div>
      )}

      {/* 選択中LPのトラッキング情報 */}
      {selectedLp && !newlyCreatedLp && (
        <div className="lp-tracker-info">
          <p><strong>トラッキングタグ:</strong></p>
          <div className="lp-tag-code-wrapper">
            <code>{`<script src="${getServerUrl()}/tracker/heatmap-tracker.js" data-lp-id="${selectedLp}"></script>`}</code>
            <button className="lp-copy-btn" onClick={() => copyTag(selectedLp)}>
              {copied ? 'コピー済み!' : 'コピー'}
            </button>
          </div>
          <p className="lp-preview-link">
            プレビュー: <a href={`${getServerUrl()}/lp/${selectedLp}`} target="_blank" rel="noreferrer">{getServerUrl()}/lp/{selectedLp}</a>
          </p>
        </div>
      )}
    </div>
  );
}

export default LPManager;
